import { Receipt } from "@phosphor-icons/react";
import LegalPageShell from "@/components/LegalPageShell";
import { SUPPORT_EMAIL, supportMailto } from "@/constants/support";

/**
 * PawLink Refund Policy — required for Paddle domain approval.
 *
 * This page is deliberately English-only (a lightweight legal artefact
 * that Paddle & payment processors read). It complements the Terms of Use
 * §5 ("PawLink Protection subscription") and the Privacy Policy.
 *
 * URLs:
 *   /refund-policy   (canonical, linked from footer + Paddle checkout)
 *   /refunds         (short alias)
 */
export default function RefundPolicy() {
    return (
        <LegalPageShell
            testId="refund-policy-page"
            kicker="Legal"
            title="Refund Policy"
            sub="Clear, honest rules for cancelling and refunding your PawLink Protection subscription."
            updated="February 2026"
            icon={<Receipt size={28} weight="fill" />}
        >
            <p>
                This Refund Policy describes when and how you can obtain a refund for a PawLink
                Protection subscription purchased on <strong>getpawlink.com</strong>. It applies to
                every subscription processed by our Merchant of Record, <strong>Paddle.com Market
                Limited</strong> (&ldquo;Paddle&rdquo;), which handles all payments on our behalf.
            </p>

            <p>
                <strong>PawLink Protection is a digital software subscription.</strong> PawLink does
                not sell, ship, deliver, manufacture, or supply any physical product as part of the
                subscription. The Smart QR is a digital feature generated by our software and linked
                to your pet&rsquo;s online profile; users may independently download or print their
                own QR for personal use.
            </p>

            <h2>1. Subscription overview</h2>
            <ul>
                <li>PawLink Protection is offered on two recurring plans: <strong>9.90 CHF / month</strong> and <strong>79.00 CHF / year</strong>.</li>
                <li>All prices are shown in Swiss Francs (CHF) before checkout. Paddle may convert the amount to your local currency at checkout — the local price is always displayed before you pay.</li>
                <li>Applicable sales tax, VAT, or GST is calculated and displayed by Paddle at checkout based on your billing country.</li>
                <li>Your subscription automatically renews at the end of each billing period until you cancel.</li>
            </ul>

            <h2>2. 14-day cooling-off refund (EU / UK / EEA consumers)</h2>
            <p>
                If you are a consumer resident in the European Union, the United Kingdom, or the
                European Economic Area, you have the right to a full refund within{" "}
                <strong>14 calendar days</strong> of the day your Protection subscription was
                purchased or renewed, no questions asked. You may exercise this right even if you
                have started using premium features.
            </p>
            <p>
                To request this refund, email <a href={supportMailto({ subject: "PawLink Refund Request — 14-day cooling-off" })}>{SUPPORT_EMAIL}</a>
                {" "}from the address associated with your PawLink account, or open a ticket via{" "}
                <a href="/support">Contact Support</a>. We will confirm the refund within{" "}
                <strong>5 business days</strong> and issue it back to the original payment method.
                Paddle typically posts the refund within 5&ndash;10 business days depending on your
                bank.
            </p>

            <h2>3. Refund for the current billing period (all other regions)</h2>
            <p>
                Outside the EU/UK/EEA, we offer a <strong>7-day satisfaction guarantee</strong>. If
                you are not happy with PawLink Protection within 7 calendar days of your first
                purchase, contact us and we will refund the amount charged for that period in full.
            </p>

            <h2>4. Refund on accidental duplicate charges</h2>
            <p>
                If you were charged twice for the same billing period due to a technical error, we
                will refund the duplicate charge in full regardless of how much time has passed.
                Include the two Paddle order IDs (they start with <code>chk_</code> or
                <code>ord_</code>) in your refund email so we can process it fastest.
            </p>

            <h2>5. Renewal refunds</h2>
            <p>
                Your Protection subscription renews automatically. Because we send a renewal
                reminder email at least 7 days before each renewal, renewals are considered
                intentional. If you forgot to cancel and your subscription just renewed:
            </p>
            <ul>
                <li>Contact us within <strong>7 calendar days</strong> of the renewal date and we will refund the renewal charge in full, provided the subscription has not been used to protect an actively lost or missing pet during that window.</li>
                <li>After 7 days, renewals are non-refundable, but you may cancel at any time to prevent the next renewal — access continues until the end of the paid period.</li>
            </ul>

            <h2>6. What is not refundable</h2>
            <ul>
                <li>Requests submitted more than 14 days (EU/UK/EEA) or 7 days (rest of world) after the initial purchase or renewal date, except duplicate-charge cases (Section 4).</li>
                <li>Free-tier PawLink usage — the free plan is, and always will be, free of charge.</li>
                <li>Chargebacks issued by your bank without contacting us first. If you have any issue, please email <a href={supportMailto({ subject: "PawLink Payment Question" })}>{SUPPORT_EMAIL}</a> first — we resolve almost every issue within one business day.</li>
            </ul>

            <h2>7. Cancelling your subscription</h2>
            <p>
                You can cancel at any time and keep Protection active until the end of the current
                paid period. Two easy ways:
            </p>
            <ol>
                <li>Sign in to PawLink, open <a href="/subscription">Protection</a>, and use the &ldquo;Cancel subscription&rdquo; button.</li>
                <li>Reply to any Paddle receipt email (Paddle also provides a self-service cancellation link inside every receipt).</li>
            </ol>
            <p>
                After cancellation, your pet profiles, QR codes, and account remain fully functional
                on the free plan &mdash; only premium features (multi-pet, voice reply, priority
                alerts) are turned off at the period end.
            </p>

            <h2>8. How to request a refund</h2>
            <p>
                Email <a href={supportMailto({ subject: "PawLink Refund Request" })}>{SUPPORT_EMAIL}</a> from the address on your PawLink account. Please include:
            </p>
            <ul>
                <li>The Paddle order ID (starts with <code>chk_</code> / <code>ord_</code>) &mdash; found in the payment receipt Paddle emailed you.</li>
                <li>The reason for the request (helps us improve the product; it does not affect eligibility).</li>
            </ul>
            <p>
                We reply to every refund email within <strong>1 business day</strong> and issue the
                refund via Paddle within <strong>5 business days</strong> of approval.
            </p>

            <h2>9. Merchant of Record</h2>
            <p>
                All PawLink Protection subscriptions are sold and refunded by Paddle.com Market
                Limited, our Merchant of Record. You may also review Paddle&rsquo;s own buyer
                terms at <a href="https://www.paddle.com/legal/checkout-buyer-terms" target="_blank" rel="noopener noreferrer">paddle.com/legal/checkout-buyer-terms</a>.
                Refunds authorised by PawLink are processed directly by Paddle back to your
                original payment method.
            </p>

            <h2>10. Contact</h2>
            <p>
                Questions about this Refund Policy? Email{" "}
                <a href={supportMailto({ subject: "PawLink Refund Policy question" })}>{SUPPORT_EMAIL}</a>{" "}
                or open a ticket from <a href="/support">Contact Support</a>. We&rsquo;re a small
                team of pet owners and we take every payment question seriously.
            </p>
        </LegalPageShell>
    );
}
